import { useEffect, useRef, useState } from 'react';

import { Box, Stack, Tooltip, Typography } from '@mui/material';

import { RenderFormField } from '@/components';
import { colors } from '@/theme/variables';
import {
  formatPhoneNumber,
  formatPhoneNumberType,
  IMasterNamePhoneNumberOption,
} from '@/utils';

import { ReporterContactPhoneField as ReporterContactPhoneFieldConfig } from './data';

interface ReporterContactPhoneFieldProps {
  additionalPhoneNumbers: IMasterNamePhoneNumberOption[];
  onChange: (fieldName: string, value: string | number) => void;
  readOnly: boolean;
}

export const ReporterContactPhoneField = ({
  additionalPhoneNumbers,
  onChange,
  readOnly,
}: ReporterContactPhoneFieldProps) => {
  const labelRef = useRef<HTMLSpanElement | null>(null);
  const [showLabelTooltip, setShowLabelTooltip] = useState(false);

  useEffect(() => {
    const measureOverflow = () => {
      const labelElement = labelRef.current;
      if (!labelElement) {
        setShowLabelTooltip(false);
        return;
      }

      setShowLabelTooltip(labelElement.scrollWidth > labelElement.clientWidth);
    };

    measureOverflow();
    window.addEventListener('resize', measureOverflow);

    return () => {
      window.removeEventListener('resize', measureOverflow);
    };
  }, [additionalPhoneNumbers.length]);

  const additionalNumbersBadge =
    additionalPhoneNumbers.length > 0 ? (
      <Tooltip
        arrow
        placement="bottom"
        slotProps={{
          tooltip: {
            sx: {
              bgcolor: colors.grey[90],
              color: colors.white,
              borderRadius: 2,
              px: 1.5,
              py: 0.75,
              maxWidth: 'none',
              '& .MuiTypography-root': {
                fontSize: 14,
                lineHeight: 1.4,
                fontWeight: 500,
                color: colors.white,
              },
            },
          },
          arrow: {
            sx: {
              color: colors.grey[90],
            },
          },
        }}
        title={
          <Stack spacing={0.25}>
            {additionalPhoneNumbers.map((phoneNumber, index) => (
              <Typography
                key={`${phoneNumber.type}-${phoneNumber.phoneNumber}-${index}`}
                variant="body2"
              >
                {`${formatPhoneNumberType(
                  phoneNumber.type,
                )}: ${formatPhoneNumber(phoneNumber.inputValue)}`}
              </Typography>
            ))}
          </Stack>
        }
      >
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 32,
            flexShrink: 0,
            px: 0.75,
            py: 0.125,
            borderRadius: 3,
            bgcolor: colors.error.main,
            color: colors.white,
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600,
            lineHeight: '16px',
          }}
        >
          {`+${additionalPhoneNumbers.length}`}
        </Box>
      </Tooltip>
    ) : null;

  return (
    <Box>
      <RenderFormField
        {...ReporterContactPhoneFieldConfig}
        handleChange={onChange}
        label={
          <Box
            component="span"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
            }}
          >
            <Tooltip
              title={
                showLabelTooltip ? ReporterContactPhoneFieldConfig.label : ''
              }
              disableHoverListener={!showLabelTooltip}
              disableFocusListener={!showLabelTooltip}
              disableTouchListener={!showLabelTooltip}
            >
              <Box
                component="span"
                ref={labelRef}
                sx={{
                  minWidth: 0,
                  maxWidth: additionalNumbersBadge
                    ? 'calc(100% - 40px)'
                    : '100%',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {ReporterContactPhoneFieldConfig.label}
              </Box>
            </Tooltip>
            {additionalNumbersBadge}
          </Box>
        }
        readOnly={readOnly}
      />
    </Box>
  );
};
